import { useEffect, useMemo, useState } from 'react';
import { CpmBadge, CpmButton, CpmCheckbox } from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    getStoreBrandDetails,
    updateStoreBrandReports,
} from '../../../services/reportsAdminService';
import type {
    FormPhase,
    StoreBrandDetails,
    StoreBrandReportRow,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { canTurnOn, formatLastToggleLabel, groupByCategory, plural } from '../helpers';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsSkeletonList,
    ReportsWait,
} from '../components/ReportsDialog';
import CategoryGroupHeader from '../components/CategoryGroupHeader';
import FilteredEmpty from '../components/FilteredEmpty';
import FilterToggleChip from '../components/FilterToggleChip';
import InfoI from '../components/InfoI';
import KindBadge from '../components/KindBadge';
import SearchField from '../components/SearchField';
import ApplyTemplateModal from '../Templates/ApplyTemplateModal';
import NameOverrideRow from './NameOverrideRow';
import WhatsAppPicksModal from './WhatsAppPicksModal';

const ACTION_BTN = '!text-[var(--cpm-text-primary)] hover:!text-[var(--cpm-brand-navy)]';

interface StoreBrandReportsModalProps {
    storeBrandID: string;
    onClose: () => void;
    onSaved: () => void;
}

export default function StoreBrandReportsModal({
    storeBrandID,
    onClose,
    onSaved,
}: StoreBrandReportsModalProps) {
    const [phase, setPhase] = useState<FormPhase>('loading');
    const [details, setDetails] = useState<StoreBrandDetails | null>(null);
    const [savedIds, setSavedIds] = useState<string[]>([]);
    const [ticked, setTicked] = useState<string[]>([]);
    const [currentlyOn, setCurrentlyOn] = useState(false);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');
    const [receipt, setReceipt] = useState('');
    const [waOpen, setWaOpen] = useState(false);
    const [applyOpen, setApplyOpen] = useState(false);
    const [waCount, setWaCount] = useState(0);

    const locked = phase === 'saving';
    const stateLabel = details?.brand.state || details?.brand.mixedStates?.[0] || '';

    const load = async () => {
        setPhase('loading');
        setError('');
        try {
            const data = await getStoreBrandDetails(storeBrandID);
            const enabled = data.reports.filter((row) => row.enabled).map((row) => row.reportID);
            setDetails(data);
            setSavedIds(enabled);
            setTicked(enabled);
            setWaCount(data.whatsappPickCount ?? 0);
            setPhase('idle');
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('fetchError');
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeBrandID]);

    const reports = useMemo(
        () => (details?.reports ?? []).filter((row) => row.kind !== 'ALWAYS_ON'),
        [details]
    );
    const q = search.trim().toLowerCase();
    const listed = reports.filter((row) => {
        if (currentlyOn && !ticked.includes(row.reportID)) return false;
        return true;
    });
    const visible = listed.filter((row) => {
        if (q && !row.name.toLowerCase().includes(q)) return false;
        return true;
    });
    const groups = groupByCategory(visible);
    const lockedN = reports.filter((row) => !row.enabled && !canTurnOn(row)).length;

    const turnOn = useMemo(
        () => ticked.filter((id) => !savedIds.includes(id)).length,
        [savedIds, ticked]
    );
    const turnOff = useMemo(
        () => savedIds.filter((id) => !ticked.includes(id)).length,
        [savedIds, ticked]
    );
    const dirty = turnOn + turnOff > 0;
    const onlyEmpty = currentlyOn && listed.length === 0 && reports.length > 0 && !q;
    const filteredEmpty = listed.length > 0 && visible.length === 0;

    const persist = async () => {
        if (!dirty) {
            setReceipt(REPORTS_COPY.SAVED_NO_TICK);
            return;
        }
        setPhase('saving');
        setError('');
        try {
            await updateStoreBrandReports({ storeBrandID, reportIds: ticked });
            setSavedIds(ticked);
            setDetails((prev) =>
                prev
                    ? {
                          ...prev,
                          enabledCount: ticked.length,
                          reports: prev.reports.map((row) => ({
                              ...row,
                              enabled: ticked.includes(row.reportID),
                          })),
                      }
                    : prev
            );
            setReceipt(`Saved. Turned on ${turnOn} · turned off ${turnOff}`);
            setPhase('idle');
            onSaved();
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const toggle = (row: StoreBrandReportRow) => {
        if (locked) return;
        const on = ticked.includes(row.reportID);
        if (!on && !canTurnOn(row)) return;
        setReceipt('');
        setTicked((prev) =>
            on ? prev.filter((id) => id !== row.reportID) : [...prev, row.reportID]
        );
    };

    const lastToggle = details?.brand.lastToggledAt
        ? formatLastToggleLabel(details.brand.lastToggledAt)
        : 'No toggle recorded for this store brand';

    const subtitle = stateLabel
        ? `What this store brand has on, and what the catalogue can offer it in ${stateLabel}. Reports scoped to another state are not on the table — this store brand cannot be given them.`
        : 'Which catalogue reports are on for this store brand. Always on is already available everywhere and is not a tick.';

    const statsNote =
        (details?.enabledCount ?? 0) === 0
            ? `0 on is a number, not a failed request and not an empty system. ${plural(details?.availableCount ?? 0, 'report')} can be turned on${stateLabel ? ` for ${stateLabel}` : ''}.`
            : `${plural(details?.enabledCount ?? 0, 'report')} on out of ${details?.availableCount ?? 0} the catalogue offers${stateLabel ? ` in ${stateLabel}` : ''}. Reports for other states are not counted here.`;

    const tickNote =
        'One list of every report this store brand can have. Tick = on, untick = off. Ticking writes nothing — one Save at the bottom writes the ticks. Always-on reports are already available to every store brand and are not in the list; a report that cannot be turned on carries a lock instead of a tick, and a leftover tick on one can always be taken off.' +
        (currentlyOn
            ? ' Currently on is applied, so only the reports already on for this store brand are listed.'
            : '');

    const listCount =
        `${plural(visible.length, 'report')} listed · ${savedIds.length} on now` +
        (lockedN ? ` · ${lockedN} cannot be turned on` : '');

    const previewMain = dirty
        ? `Turn on ${turnOn} · turn off ${turnOff}`
        : REPORTS_COPY.NO_TICK_CHANGE;
    const previewSub = dirty
        ? `${plural(turnOn, 'report')} would go on and ${plural(turnOff, 'report')} would come off for this store brand. Nothing is written until you Save, and templates are never part of this write.`
        : `Save still works: it writes the ticks you have, which are the ${savedIds.length} reports already on for this store brand. No template is touched.`;

    const stateBadges = [
        ...(details?.brand.state ? [details.brand.state] : []),
        ...(!details?.brand.state && details?.brand.mixedStates?.length
            ? details.brand.mixedStates
            : []),
    ];

    return (
        <>
            <ReportsDialog
                open
                width={900}
                zIndex={320}
                onClose={locked || waOpen || applyOpen ? () => undefined : onClose}
                closeOnBackdrop={!locked && !waOpen && !applyOpen}
                closeOnEscape={!locked && !waOpen && !applyOpen}
            >
                <ReportsDialogHeader
                    kicker="Store brand"
                    title={details?.brand.name || 'Store brand'}
                    titleExtra={
                        stateBadges.length ? (
                            <span className="inline-flex flex-wrap items-center" style={{ gap: 6 }}>
                                {stateBadges.map((state) => (
                                    <CpmBadge key={state} tone="neutral">
                                        {state}
                                    </CpmBadge>
                                ))}
                            </span>
                        ) : null
                    }
                    subtitle={subtitle}
                    meta={
                        <div
                            style={{
                                marginTop: 4,
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-tertiary)',
                            }}
                        >
                            {lastToggle}
                        </div>
                    }
                    onClose={locked || waOpen || applyOpen ? undefined : onClose}
                />
                <ReportsDialogBody pad="picker">
                    {phase === 'loading' ? (
                        <div>
                            <div style={{ paddingBottom: 16 }}>
                                <ReportsWait label="Getting what this store brand has on, and what the catalogue offers for its state. The dialog is already open — this wait belongs to it, not to the list behind it." />
                            </div>
                            <div
                                className="cpm-sk"
                                style={{ height: 92, width: '100%', marginBottom: 14 }}
                            />
                            <ReportsSkeletonList rows={4} />
                        </div>
                    ) : null}
                    {phase === 'fetchError' ? (
                        <ReportsAlert
                            tone="danger"
                            title="Couldn't load this store brand"
                            body="The on and available reports for this store brand did not come back, so nothing here is a count of zero. Nothing was turned on or off, the store brand list behind this dialog is exactly as it was, and Catalogue and Templates were not part of this request."
                            reason={reportsAdminErrorReason(error)}
                            action={
                                <CpmButton variant="outline" size="sm" onClick={() => void load()}>
                                    Retry this store brand
                                </CpmButton>
                            }
                        />
                    ) : null}
                    {details && phase !== 'loading' && phase !== 'fetchError' ? (
                        <div>
                            {error ? (
                                <div style={{ marginBottom: 14 }}>
                                    <ReportsAlert
                                        tone="danger"
                                        title="Couldn’t save the reports for this store brand"
                                        body="Nothing changed for this store brand and your ticks are still here. Templates were never part of this write."
                                        reason={reportsAdminErrorReason(
                                            error,
                                            'Nothing changed for this store brand and your ticks are still here.'
                                        )}
                                        action={
                                            <CpmButton
                                                variant="outline"
                                                size="sm"
                                                onClick={() => void persist()}
                                            >
                                                Retry the save
                                            </CpmButton>
                                        }
                                    />
                                </div>
                            ) : null}
                            {receipt ? (
                                <div style={{ marginBottom: 14 }}>
                                    <ReportsAlert
                                        tone="success"
                                        title={receipt}
                                        body={`On now reads ${details.enabledCount} for this store brand — zero would be a number too. No template changed, and this store brand stores no template.`}
                                    />
                                </div>
                            ) : null}

                            <div className="flex flex-wrap" style={{ gap: 10 }}>
                                <Stat label="Enabled now" value={details.enabledCount} />
                                <Stat
                                    label={
                                        stateLabel
                                            ? `Available in ${stateLabel}`
                                            : 'Available for this state'
                                    }
                                    value={details.availableCount}
                                />
                                <Stat label="Picked for daily WhatsApp" value={waCount} />
                            </div>
                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    lineHeight: 1.45,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {statsNote}
                            </div>
                            {details.alwaysOnLine ? (
                                <div
                                    style={{
                                        marginTop: 5,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color: 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    Always-on reports are already available to every store brand, so
                                    they are not listed here.
                                </div>
                            ) : null}

                            <div
                                className="flex flex-wrap items-center"
                                style={{
                                    gap: 9,
                                    marginTop: 14,
                                    paddingBottom: 16,
                                    borderBottom: '1px solid var(--cpm-border-default)',
                                }}
                            >
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    className={ACTION_BTN}
                                    disabled={locked}
                                    onClick={() => setApplyOpen(true)}
                                >
                                    Apply a template to this store brand
                                </CpmButton>
                                <CpmButton
                                    variant="outline"
                                    size="sm"
                                    className={ACTION_BTN}
                                    disabled={locked}
                                    onClick={() => setWaOpen(true)}
                                >
                                    Daily WhatsApp report picks
                                </CpmButton>
                            </div>

                            <div style={{ marginTop: 16, maxWidth: 620 }}>
                                <div className="flex items-center" style={{ gap: 7 }}>
                                    <span
                                        className="uppercase"
                                        style={{
                                            fontSize: 9.5,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: 'var(--cpm-text-tertiary)',
                                        }}
                                    >
                                        Which reports are on for this store brand
                                    </span>
                                    <InfoI
                                        title={REPORTS_TIPS.TURN_ON_OR_OFF.title}
                                        body={REPORTS_TIPS.TURN_ON_OR_OFF.body}
                                    />
                                </div>
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        lineHeight: 1.45,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {tickNote}
                                </div>
                            </div>

                            <div
                                className="flex flex-wrap items-center"
                                style={{ gap: 9, marginTop: 14 }}
                            >
                                <SearchField
                                    value={search}
                                    onChange={setSearch}
                                    placeholder="Search this store brand's reports by name"
                                    disabled={locked}
                                    width={300}
                                    size="sm"
                                />
                                <FilterToggleChip
                                    label={REPORTS_COPY.CURRENTLY_ON_FILTER}
                                    on={currentlyOn}
                                    disabled={locked}
                                    onClick={() => setCurrentlyOn((v) => !v)}
                                />
                                <span
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {dirty
                                        ? `${turnOn} to turn on · ${turnOff} to turn off`
                                        : 'No tick change yet'}
                                </span>
                                <CpmButton
                                    variant="ghost"
                                    size="sm"
                                    disabled={locked || !dirty}
                                    onClick={() => {
                                        setTicked(savedIds);
                                        setReceipt('');
                                    }}
                                >
                                    {REPORTS_COPY.DISCARD_TICKS}
                                </CpmButton>
                            </div>

                            <div
                                style={{
                                    marginTop: 10,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    color: 'var(--cpm-text-primary)',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {listCount}
                            </div>

                            {onlyEmpty ? (
                                <div
                                    style={{
                                        marginTop: 14,
                                        padding: 22,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                        background: 'var(--cpm-surface-subtle)',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        This store brand has 0 reports on
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 5,
                                            fontSize: 12.5,
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        A loaded number, not a failure and not an empty catalogue.
                                        Turn Currently on off to see every report available
                                        {stateLabel ? ` in ${stateLabel}` : ''} and tick the ones
                                        this store brand should have.
                                    </div>
                                </div>
                            ) : null}

                            {filteredEmpty ? (
                                <div style={{ marginTop: 14 }}>
                                    <FilteredEmpty
                                        title="No report here matches that search"
                                        cause="The search caused this, not an empty store brand."
                                        restated={`The search is “${search.trim()}”. ${plural(listed.length, 'report')}${currentlyOn ? ' are on for this store brand.' : ' are in this list.'}`}
                                        onClear={() => setSearch('')}
                                    />
                                </div>
                            ) : null}

                            {!onlyEmpty && !filteredEmpty && visible.length > 0 ? (
                                <div className="flex flex-col" style={{ gap: 14, marginTop: 14 }}>
                                    {groups.map((group) => (
                                        <div
                                            key={group.name}
                                            style={{
                                                border: '1px solid var(--cpm-border-default)',
                                                borderRadius: 10,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <CategoryGroupHeader
                                                name={group.name}
                                                count={group.rows.length}
                                            />
                                            {group.rows.map((row) => {
                                                const on = ticked.includes(row.reportID);
                                                const allowed = canTurnOn(row);
                                                const leftover = on && !allowed;
                                                const lockedTick = !allowed && !leftover;
                                                const changed = on !== row.enabled;
                                                const extraCats = row.categories.filter(
                                                    (category) => category !== group.name
                                                );
                                                return (
                                                    <div
                                                        key={row.reportID}
                                                        style={{
                                                            borderTop:
                                                                '1px solid var(--cpm-border-default)',
                                                        }}
                                                    >
                                                        <div
                                                            className="grid items-center"
                                                            onClick={() => toggle(row)}
                                                            style={{
                                                                gridTemplateColumns:
                                                                    '24px 1fr 250px 118px',
                                                                gap: 14,
                                                                padding: '9px 16px 9px 14px',
                                                                cursor: lockedTick
                                                                    ? 'default'
                                                                    : 'pointer',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background =
                                                                    'var(--cpm-purple-tint-3)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background =
                                                                    'transparent';
                                                            }}
                                                        >
                                                            <div className="flex items-center justify-center">
                                                                {lockedTick ? (
                                                                    <LockIcon />
                                                                ) : (
                                                                    <span
                                                                        onClick={(e) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    >
                                                                        <CpmCheckbox
                                                                            checked={on}
                                                                            disabled={locked}
                                                                            onChange={() =>
                                                                                toggle(row)
                                                                            }
                                                                        />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div
                                                                className="flex items-center min-w-0"
                                                                style={{ gap: 8 }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: 13.5,
                                                                        fontWeight: 800,
                                                                        letterSpacing: '-0.01em',
                                                                        color: 'var(--cpm-text-primary)',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {row.name}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className="flex flex-wrap items-center"
                                                                style={{ gap: 6 }}
                                                            >
                                                                {extraCats.map((category) => (
                                                                    <span
                                                                        key={category}
                                                                        style={{
                                                                            padding: '3px 8px',
                                                                            borderRadius:
                                                                                'var(--cpm-radius-6)',
                                                                            background:
                                                                                'var(--cpm-surface-tag)',
                                                                            fontSize: 10.5,
                                                                            fontWeight: 700,
                                                                            color: 'var(--cpm-text-secondary)',
                                                                        }}
                                                                    >
                                                                        {category}
                                                                    </span>
                                                                ))}
                                                                {row.kind !== 'GENERATED' ? (
                                                                    <KindBadge kind={row.kind} />
                                                                ) : null}
                                                                {changed ? (
                                                                    <CpmBadge tone="brand">
                                                                        {on
                                                                            ? 'Will go on'
                                                                            : 'Will come off'}
                                                                    </CpmBadge>
                                                                ) : null}
                                                                {lockedTick ? (
                                                                    <span
                                                                        style={{
                                                                            fontSize: 10.5,
                                                                            fontWeight: 700,
                                                                            color: 'var(--cpm-text-tertiary)',
                                                                        }}
                                                                    >
                                                                        {lockLabel(row)} — cannot be
                                                                        turned on
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            <ReportIdCell id={row.reportID} />
                                                        </div>
                                                        {on && row.kind === 'GENERATED' ? (
                                                            <div
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{
                                                                    padding: '0 16px 12px 52px',
                                                                }}
                                                            >
                                                                <NameOverrideRow
                                                                    storeBrandID={storeBrandID}
                                                                    reportID={row.reportID}
                                                                    catalogueName={row.name}
                                                                    value={row.nameOverride}
                                                                    onSaved={(next) => {
                                                                        setDetails((prev) =>
                                                                            prev
                                                                                ? {
                                                                                      ...prev,
                                                                                      reports:
                                                                                          prev.reports.map(
                                                                                              (
                                                                                                  item
                                                                                              ) =>
                                                                                                  item.reportID ===
                                                                                                  row.reportID
                                                                                                      ? {
                                                                                                            ...item,
                                                                                                            nameOverride:
                                                                                                                next,
                                                                                                        }
                                                                                                      : item
                                                                                          ),
                                                                                  }
                                                                                : prev
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </ReportsDialogBody>
                <ReportsDialogFooter
                    previewMain={previewMain}
                    previewSub={previewSub}
                    saving={locked}
                    savingLabel="Saving which reports are on for this store brand. The write has not finished — do not fire it again, and this dialog stays put until it does."
                    onCancel={onClose}
                    cancelLabel="Close"
                    primaryLabel={REPORTS_COPY.SAVE}
                    onPrimary={() => void persist()}
                    primaryDisabled={phase === 'loading' || phase === 'fetchError'}
                />
            </ReportsDialog>
            {waOpen ? (
                <WhatsAppPicksModal
                    storeBrandID={storeBrandID}
                    brandName={details?.brand.name}
                    state={details?.brand.state}
                    reports={reports}
                    onClose={() => setWaOpen(false)}
                    onSaved={setWaCount}
                />
            ) : null}
            {applyOpen ? (
                <ApplyTemplateModal
                    source="brands"
                    presetStoreBrands={[
                        {
                            storeBrandId: storeBrandID,
                            name: details?.brand.name,
                            state: details?.brand.state,
                        },
                    ]}
                    onClose={() => setApplyOpen(false)}
                    onApplied={() => {
                        setApplyOpen(false);
                        void load();
                        onSaved();
                    }}
                />
            ) : null}
        </>
    );
}

function lockLabel(row: StoreBrandReportRow): string {
    if (row.kind === 'DEAD') return REPORTS_COPY.KIND_DEAD;
    if (!row.generatorBound) return 'No builder linked';
    return 'Inactive';
}

function LockIcon() {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cpm-text-tertiary)"
            strokeWidth="2"
        >
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
    );
}

function ReportIdCell({ id }: { id: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            title="Click to copy the full id"
            onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard.writeText(id);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1200);
            }}
            className="bg-transparent border-0 cursor-copy"
            style={{
                textAlign: 'right',
                fontFamily: 'var(--cpm-font-mono)',
                fontSize: 10.5,
                fontWeight: 700,
                color: 'var(--cpm-text-tertiary)',
                width: '100%',
                padding: 0,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--cpm-brand-purple)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--cpm-text-tertiary)';
            }}
        >
            {copied ? REPORTS_COPY.COPIED : `${id.slice(0, 8)}…${id.slice(-4)}`}
        </button>
    );
}

function Stat({ label, value }: { label: string; value: number }) {
    return (
        <div
            style={{
                flex: '1 1 150px',
                padding: '12px 14px',
                background: 'var(--cpm-surface-subtle)',
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 8,
            }}
        >
            <div
                style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {value}
            </div>
            <div
                className="uppercase"
                style={{
                    marginTop: 3,
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    color: 'var(--cpm-text-tertiary)',
                }}
            >
                {label}
            </div>
        </div>
    );
}
